import type { CapsuleOptions, ProductShell } from './api.ts'

/**
 * What the product page shows and changes about a capsule shell (#608, #636),
 * worked out before it draws. Pure, so the rules -- the account's word wins,
 * one trait per axis -- are tested here as well as enforced by the server.
 */

/** Whether a verification reads this product as a shell: by its name, or by the account's mark. */
export function isShell(shell: ProductShell): boolean {
  return shell.byName || shell.corrected !== null
}

/** What a shell counts as now: the account's word when it gave one, its name otherwise. */
export function shellTraitsInForce(shell: ProductShell): readonly string[] {
  return shell.corrected?.traits ?? shell.read
}

/**
 * The traits chosen on each axis, as one list for the server: an axis left on
 * "não diz" adds nothing, so a shell is at most one trait per axis.
 */
export function chosenTraits(byAxis: Readonly<Record<string, string>>): string[] {
  return Object.values(byAxis).filter((trait) => trait !== '')
}

/** The trait a list holds on one axis, or '' for none. */
export function traitOnAxis(
  traits: readonly string[],
  axis: string,
  catalogue: CapsuleOptions['traits'],
): string {
  return traits.find((trait) => catalogue.find((one) => one.id === trait)?.axis === axis) ?? ''
}

/** The screen's words for some traits, in the order given. */
export function traitLabels(
  traits: readonly string[],
  catalogue: CapsuleOptions['traits'],
): string[] {
  return traits.map((trait) => catalogue.find((one) => one.id === trait)?.label ?? trait)
}

/** Whether the choice on screen differs from what the shell counts as now. */
export function shellChoiceChanged(shell: ProductShell, chosen: readonly string[]): boolean {
  const now = shellTraitsInForce(shell)
  // A product that is no shell yet becomes one on save, even with nothing chosen.
  if (!isShell(shell)) return true
  return now.length !== chosen.length || now.some((trait) => !chosen.includes(trait))
}
